import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  InputAdornment,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// * Utils
import { getInitiativeInclusionsForNode } from '@/features/energyxplorer/utils/energyTreeNodeInitiativeInclusion';
import {
  getGroupedOptions,
  getTypeColor,
  getTypeIcon,
} from '@/utils/nodeTypeMapper';
import {
  filterOptionGroupsForEdit,
  isResidualNode,
  mapUnitsToDataStreams,
} from '../../utils/energyTreeFunctions';

// * MUI Icons
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';

// * API
import { usePutEnergyTreeNodesMutation } from '@/api/recalcV2/endpoints/energyTreeNodesApiSlice';

// * Redux
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import { useDispatch, useSelector } from 'react-redux';

// ✅ New middleware (soft, hierarchy-focused validation)
import { validateEditNode } from '../../utils/energyTreeNodesValidationMiddleware';

import InitiativeInclusionNotice from './InitiativeInclusionNotice';
import ValidationNotice from './ValidationNotice';

const EditNodeDialog = ({
  open,
  onClose,
  clickedNode,
  units,
  tree,
  energyGroup,
}) => {
  const { t } = useTranslation('energyxplorer-tree-tab', 'common');
  const account = useSelector(getSelectedAccount);
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState('');
  const [putEnergyTreeNodes] = usePutEnergyTreeNodesMutation();

  const [formState, setFormState] = useState({
    energyGroupId: clickedNode?.energyGroupId || null,
    name: clickedNode?.name || '',
    type: clickedNode?.type || '',
    energyTreeNodeId: clickedNode?.energyTreeNodeId || null,
    parentId: clickedNode?.parentId || null,
    dataStreams: clickedNode?.dataStreams || [],
    options: clickedNode?.options || null,
  });

  const inclusions = useMemo(() => {
    const nodesFound = getInitiativeInclusionsForNode(
      clickedNode.energyTreeNodeId,
      energyGroup,
    );
    return nodesFound;
  }, [clickedNode.energyTreeNodeId, energyGroup]);

  const virtualInitiativeInclusions = useMemo(
    () =>
      (inclusions || []).filter(
        ({ viaNode }) =>
          viaNode && (!viaNode.dataStreams || viaNode.dataStreams.length === 0),
      ),
    [inclusions],
  );

  useEffect(() => {
    if (open) {
      // Dialog just opened → sync relevant props
      setFormState({
        energyGroupId: clickedNode?.energyGroupId || null,
        name: clickedNode?.name || '',
        type: clickedNode?.type || '',
        energyTreeNodeId: clickedNode?.energyTreeNodeId || null,
        parentId: clickedNode?.parentId || null,
        dataStreams: clickedNode?.dataStreams || [],
        options: clickedNode?.options || null,
      });
    } else {
      // Dialog just closed → reset completely
      setFormState({
        energyGroupId: null,
        name: '',
        type: '',
        dataStreams: [],
        energyTreeNodeId: null,
        parentId: null,
        options: {
          specialNodeType: null,
          power: null,
          percentage: null,
        },
      });
    }
  }, [
    open,
    clickedNode?.energyTreeNodeId,
    clickedNode?.energyGroupId,
    clickedNode?.name,
    clickedNode?.type,
    clickedNode?.parentId,
    clickedNode?.dataStreams,
    clickedNode?.options,
  ]);

  const [residualSource, setResidualSource] = useState(
    formState?.options?.specialNodeType || null,
  );

  // ✅ Use new middleware ONLY for feedback, not blocking
  const validationResult = useMemo(() => {
    if (!clickedNode || !energyGroup) return null;
    return validateEditNode({
      currentNode: clickedNode,
      targetType: formState.type,
      tree,
      dataStreams: formState.dataStreams,
    });
  }, [clickedNode, formState.type, formState.dataStreams, tree, energyGroup]);

  const allOptionGroups = useMemo(() => getGroupedOptions(), []);
  const filteredOptionGroups = useMemo(() => {
    if (!clickedNode) return allOptionGroups;
    return filterOptionGroupsForEdit(
      clickedNode,
      tree,
      allOptionGroups,
      formState.dataStreams,
    );
  }, [clickedNode, allOptionGroups, tree, formState.dataStreams]);

  const handleUpdateNode = async () => {
    if (!clickedNode) return;

    try {
      await putEnergyTreeNodes({
        accountId: account.AccountId,
        energyTreeNodeId: clickedNode.energyTreeNodeId,
        updatedEnergyTreeNode: formState,
      }).unwrap();
      dispatch(
        showSnackbar({ message: 'Node opdateret', severity: 'success' }),
      );
      onClose();
    } catch (error) {
      console.error('Failed to update node:', error);
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i opdatering af node',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" fontSize="1.25rem">
          {`Rediger node`}&nbsp;
          {clickedNode?.name}
          <Box
            sx={{
              backgroundColor: getTypeColor(clickedNode?.type),
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              ml: 1,
              height: 28,
              width: 28,
              justifyContent: 'center',
              fontSize: '14px',
            }}
          >
            <i className={`fa-solid ${getTypeIcon(clickedNode?.type)}`} />
          </Box>
        </Box>

        <i className="fa-regular fa-pen-to-square"></i>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Navn"
            value={formState.name}
            onChange={(e) =>
              setFormState({ ...formState, name: e.target.value })
            }
            fullWidth
            variant="filled"
          />

          <TextField
            select
            label="Node Type"
            value={formState.type}
            // 🔓 No hard disabling based on validation – everything is allowed,
            // middleware only gives guidance.
            onChange={(e) =>
              setFormState({ ...formState, type: e.target.value })
            }
            helperText={
              validationResult?.warnings?.length > 0
                ? 'Hierarki-hint: ' + validationResult.warnings[0]
                : ''
            }
            fullWidth
            variant="filled"
            SelectProps={{
              MenuProps: {
                PaperProps: { style: { maxHeight: 300 } },
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <i className={`fa-solid ${getTypeIcon(formState.type)}`} />
                  </InputAdornment>
                ),
              },
            }}
          >
            <MenuItem value="" disabled>
              <Typography color="text.secondary">Vælg type</Typography>
            </MenuItem>

            {Object.entries(filteredOptionGroups).map(
              ([groupLabel, options]) => {
                if (!options.length) return null;

                return [
                  <ListSubheader
                    key={`header-${groupLabel}`}
                    sx={{ bgcolor: 'background.paper' }}
                  >
                    <ListItem divider disableGutters>
                      <ListItemIcon>
                        <CategoryRoundedIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={groupLabel}
                        primaryTypographyProps={{ fontWeight: 'bold' }}
                      />
                    </ListItem>
                  </ListSubheader>,
                  ...options.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      <Box display="flex" width="100%">
                        <Box
                          sx={{
                            backgroundColor: getTypeColor(value),
                            color: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            mr: 1,
                            height: 28,
                            width: 28,
                            justifyContent: 'center',
                            fontSize: '14px',
                          }}
                        >
                          <i className={`fa-solid ${getTypeIcon(value)}`} />
                        </Box>
                        <Typography>{label}</Typography>
                      </Box>
                    </MenuItem>
                  )),
                ];
              },
            )}
          </TextField>

          {units && !isResidualNode(clickedNode) && (
            <Autocomplete
              multiple
              disableCloseOnSelect
              // 🔓 Always enabled – SEU/data rules are soft for now
              options={units}
              getOptionLabel={(option) => `${option.UnitId} - ${option.Name}`}
              value={formState.dataStreams.map((ds) =>
                units.find((unit) => unit.UnitId === ds.unitId),
              )}
              inputValue={searchText}
              onInputChange={(event, newInputValue, reason) => {
                if (reason !== 'reset') {
                  setSearchText(newInputValue);
                }
              }}
              getOptionDisabled={(option) =>
                !!option.usedInNode || !!option.notSupportedReason
              }
              onClose={() => setSearchText('')}
              onChange={(e, newValue) => {
                setFormState({
                  ...formState,
                  dataStreams: mapUnitsToDataStreams(newValue),
                });
              }}
              filterSelectedOptions
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    display={'flex'}
                    flexDirection={'row'}
                    justifyContent={'space-between'}
                    width={'100%'}
                  >
                    <Typography>
                      ({option.UnitId}) {option.Name}
                    </Typography>
                    {option.usedInNode && (
                      <Typography variant="caption" color={'text.secondary'}>
                        Anvendt for: {option.usedInNode}
                      </Typography>
                    )}
                    {option.notSupportedReason && (
                      <Typography variant="caption" color={'error'}>
                        {option.notSupportedReason}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="filled"
                  label="Vælg"
                  placeholder=" Søg"
                  helperText="" // no hard unit rules from middleware for now
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={index}
                    label={`${option.Name}`}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          )}
        </Stack>

        {isResidualNode(clickedNode) && (
          <Card
            variant="outlined"
            sx={{ p: 2, mt: 2, mb: 2, bgcolor: 'background.default' }}
          >
            <FormHelperText>
              Dette er en residual node. Det er ikke muligt at knytte
              datastrømme til denne node.
            </FormHelperText>
            <TextField
              select
              label="Rolle"
              sx={{ mt: 2 }}
              value={formState?.options?.specialNodeType || ''}
              onChange={(e) => {
                const next = e.target.value;
                setResidualSource(next);

                setFormState((fs) => {
                  const updated = { ...fs };
                  updated.options = {
                    ...updated.options,
                    specialNodeType: next,
                  };
                  if (next !== 'FlatResidual') {
                    updated.options.power = null;
                  }
                  if (next !== 'PercentageResidual') {
                    updated.options.percentage = null;
                  }
                  return updated;
                });
              }}
              fullWidth
              variant="filled"
            >
              <MenuItem value="RestResidual">Restforbrug</MenuItem>
              <MenuItem value="FlatResidual">Fast effekt (kW)</MenuItem>
              <MenuItem value="PercentageResidual">Procentandel (%)</MenuItem>
            </TextField>

            {residualSource === 'FlatResidual' && (
              <TextField
                label="Fast effekt (kW)"
                type="number"
                inputProps={{ min: 0, step: 'any' }}
                value={formState?.options?.power || ''}
                onChange={(e) => {
                  setFormState((fs) => ({
                    ...fs,
                    options: {
                      ...fs.options,
                      power:
                        e.target.value === '' ? null : Number(e.target.value),
                    },
                  }));
                }}
                variant="filled"
                fullWidth
                helperText="Angiv en fast residualeffekt i kW."
              />
            )}

            {residualSource === 'PercentageResidual' && (
              <Box sx={{ mt: 2 }}>
                <Typography gutterBottom>Procentandel (%)</Typography>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <TextField
                    type="number"
                    variant="filled"
                    hiddenLabel
                    value={(
                      (formState?.options?.percentage ?? 0) * 100
                    ).toFixed(1)}
                    onChange={(e) => {
                      const pct = parseFloat(e.target.value);
                      const clamped = isNaN(pct)
                        ? 0
                        : Math.min(Math.max(pct, 0), 100);
                      setFormState((fs) => ({
                        ...fs,
                        options: {
                          ...fs.options,
                          percentage: clamped / 100,
                        },
                      }));
                    }}
                    inputProps={{
                      min: 0,
                      max: 100,
                      step: 0.1,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <PercentRoundedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Slider
                    value={(formState?.options?.percentage || 0) * 100}
                    onChange={(_, newVal) =>
                      setFormState((fs) => ({
                        ...fs,
                        options: {
                          ...fs.options,
                          percentage: newVal / 100,
                        },
                      }))
                    }
                    min={0.1}
                    max={100}
                    step={0.1}
                  />
                </Box>
              </Box>
            )}
          </Card>
        )}

        <InitiativeInclusionNotice
          virtualInitiativeInclusions={virtualInitiativeInclusions}
          mode="edit" // or "add"
        />

        <ValidationNotice validationResult={validationResult} />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleUpdateNode}
          variant="contained"
          disabled={!clickedNode}
        >
          {t('tree_actions.save_changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditNodeDialog;
